import { useState } from 'react';
import { X, Plus, RotateCcw, SkipForward, Bug } from 'lucide-react';

interface LinkedIssue {
  id: string;
  issueId: string;
  title: string;
  status: string;
}

interface Props {
  tcId: string;
  tcTitle: string;
  linkedIssues: LinkedIssue[];
  onCreateNew: () => void;
  onReopen: (issueId: string) => void;
  onSkip: () => void;
  isPending?: boolean;
}

const STATUS_LABEL: Record<string, string> = {
  OPEN: '열림',
  IN_PROGRESS: '진행 중',
  RESOLVED: '해결됨',
  CLOSED: '닫힘',
  REOPENED: '재오픈',
};

export default function FailIssueActionModal({
  tcId,
  tcTitle,
  linkedIssues,
  onCreateNew,
  onReopen,
  onSkip,
  isPending,
}: Props) {
  const [selectedIssueId, setSelectedIssueId] = useState('');
  const hasLinkedIssues = linkedIssues.length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Bug className="size-5 text-red-500" />
            <h3 className="text-base font-semibold text-gray-900">작업 처리</h3>
          </div>
          <button
            onClick={onSkip}
            className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <X className="size-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          <div className="text-sm text-gray-600">
            <span className="font-medium text-gray-900">{tcId}</span>{' '}
            {tcTitle}
            <p className="mt-1 text-sm text-gray-400">
              FAIL 결과에 대한 작업 처리 방법을 선택하세요.
            </p>
          </div>

          {/* Option: Create New */}
          <button
            onClick={onCreateNew}
            disabled={isPending}
            className="w-full flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors text-left disabled:opacity-50"
          >
            <div className="flex-shrink-0 p-2 bg-primary-100 rounded-lg">
              <Plus className="size-5 text-primary-600" />
            </div>
            <div>
              <div className="text-sm font-medium text-gray-900">새 작업 생성</div>
              <div className="text-sm text-gray-500 mt-0.5">
                FAIL 결과를 기반으로 새 작업을 자동 생성합니다.
              </div>
            </div>
          </button>

          {/* Option: Reopen existing issues */}
          {hasLinkedIssues && (
            <div className="space-y-2">
              <div className="text-sm font-medium text-gray-700">기존 작업 재오픈</div>
              <div className="space-y-1.5">
                {linkedIssues.map((issue) => (
                  <label
                    key={issue.id}
                    className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                      selectedIssueId === issue.id
                        ? 'border-primary-400 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="reopen-issue"
                      value={issue.id}
                      checked={selectedIssueId === issue.id}
                      onChange={() => setSelectedIssueId(issue.id)}
                      className="text-primary-600 focus:ring-primary-500"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-mono text-gray-500">{issue.issueId}</span>
                        <span className="text-sm px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                          {STATUS_LABEL[issue.status] ?? issue.status}
                        </span>
                      </div>
                      <div className="text-sm text-gray-900 truncate mt-0.5">{issue.title}</div>
                    </div>
                  </label>
                ))}
              </div>
              {selectedIssueId && (
                <button
                  onClick={() => onReopen(selectedIssueId)}
                  disabled={isPending}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50 transition-colors"
                >
                  <RotateCcw className="size-4" />
                  {isPending ? '처리 중...' : '선택한 작업 재오픈'}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-200">
          <button
            onClick={onSkip}
            disabled={isPending}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            <SkipForward className="size-4" />
            건너뛰기
          </button>
        </div>
      </div>
    </div>
  );
}
